import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { usePopulateBulkDoclistPages } from "~/api/chat/chat";
import { DoclistTreeItemSectionsItem, PresignedUrls } from "~/api/model";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useChatDocumentParams } from "~/helpers";
import { cn } from "~/lib/utils";
import { useGetPageUrls } from "./useGetPageUrls";

const INITIAL_PAGES_TO_DISPLAY = 10;
const PAGES_TO_LOAD_ON_SCROLL = 20;
const SCROLL_SYNC_DEBOUNCE_MS = 50;

/**
 * Component to display a single page image with presigned URL loading
 */
interface PageImageProps {
  page: unknown;
  section: DoclistTreeItemSectionsItem;
  pageIndex: number;
  imageKey: string;
  isLoaded: boolean;
  onImageLoad: (imageKey: string) => void;
  populatePageFn: (
    pageId: string,
    sectionPageId: string
  ) => Promise<PresignedUrls>;
}

const PageImage: FC<PageImageProps> = ({
  page,
  section,
  pageIndex,
  imageKey,
  isLoaded,
  onImageLoad,
  populatePageFn,
}) => {
  // Extract page info first, before any hooks
  const pageId =
    page &&
    typeof page === "object" &&
    "id" in page &&
    typeof page.id === "string"
      ? page.id
      : "";
  const sectionPageId =
    page &&
    typeof page === "object" &&
    "sectionPageId" in page &&
    typeof page.sectionPageId === "string"
      ? page.sectionPageId
      : "";

  // Always call the hook, but disable it if we don't have valid IDs
  const { data: pageUrls, isFetching } = useGetPageUrls(
    pageId || "invalid",
    sectionPageId || "invalid",
    populatePageFn,
    !!pageId && !!sectionPageId // only enabled if we have valid IDs
  );

  // Early return after hooks
  if (!page || typeof page !== "object" || !pageId) {
    return (
      <div
        className="w-full border-2 border-dashed border-border rounded flex items-center justify-center text-muted-foreground text-sm"
        style={{ aspectRatio: "8.5 / 11" }}
      >
        <FormattedMessage
          id="noPreview"
          defaultMessage="No preview available"
        />
      </div>
    );
  }

  /**
   * Get the best available image URL from presigned URLs
   * Prioritizes scanUrl (used for LLM recognition) over higher resolutions
   */
  const getImageUrl = (): string | null => {
    if (!pageUrls) return null;

    // Priority: scanUrl > highResUrl > previewUrl
    if (pageUrls.scanUrl) return pageUrls.scanUrl;
    if (pageUrls.highResUrl) return pageUrls.highResUrl;
    if (pageUrls.previewUrl) return pageUrls.previewUrl;

    return null;
  };

  const imageUrl = getImageUrl();

  if (isFetching) {
    return (
      <Skeleton
        className="w-full bg-muted rounded"
        style={{ aspectRatio: "8.5 / 11" }}
      />
    );
  }

  if (!imageUrl) {
    return (
      <div
        className="w-full border-2 border-dashed border-border rounded flex items-center justify-center text-muted-foreground text-sm"
        style={{ aspectRatio: "8.5 / 11" }}
      >
        <FormattedMessage
          id="noPreview"
          defaultMessage="No preview available"
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {!isLoaded && (
        <Skeleton
          className="w-full bg-muted rounded absolute inset-0 z-10"
          style={{ aspectRatio: "8.5 / 11" }}
        />
      )}
      <img
        src={imageUrl}
        alt={`${section.name} - Page ${pageIndex + 1}`}
        className="w-full h-auto"
        onLoad={() => onImageLoad(imageKey)}
        onError={() => onImageLoad(imageKey)}
      />
    </div>
  );
};

interface SideBySideComparisonViewProps {
  sections: DoclistTreeItemSectionsItem[];
  onDeleteClick: (sections: DoclistTreeItemSectionsItem[]) => void;
  syncEnabled?: boolean;
  onSyncEnabledChange?: (enabled: boolean) => void;
}

interface SortableSectionProps {
  section: DoclistTreeItemSectionsItem;
  isHovered: boolean;
  pagesToShow: unknown[];
  blankPagesNeeded: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onScroll: () => void;
  scrollContainerRef: (el: HTMLDivElement | null) => void;
  contentRef: (el: HTMLDivElement | null) => void;
  loadedImages: Set<string>;
  onImageLoad: (imageKey: string) => void;
  isDraggable: boolean;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  populatePageFn: (
    pageId: string,
    sectionPageId: string
  ) => Promise<PresignedUrls>;
}

const SortableSection: FC<SortableSectionProps> = ({
  section,
  isHovered,
  pagesToShow,
  blankPagesNeeded,
  onMouseEnter,
  onMouseLeave,
  onScroll,
  scrollContainerRef,
  contentRef,
  loadedImages,
  onImageLoad,
  isDraggable,
  showCheckbox = false,
  isSelected = false,
  onCheckboxChange,
  populatePageFn,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({
      id: section.id,
      disabled: !isDraggable,
    });

  const style = transform
    ? {
        transform: CSS.Transform.toString(transform),
        touchAction: "none",
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex-shrink-0 flex flex-col border rounded-lg transition-all min-h-0 p-1 overflow-hidden",
        "w-full max-w-[48%]",
        isHovered ? "border-blue-400" : "border-border",
        isDragging && "opacity-50 z-50",
        isDraggable && "cursor-grab active:cursor-grabbing"
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...(isDraggable ? attributes : {})}
      {...(isDraggable ? listeners : {})}
    >
      <div className="flex items-center gap-2 p-2 border-b bg-muted rounded-t-md">
        {showCheckbox && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => {
              onCheckboxChange?.(checked === true);
            }}
            aria-label={`Select ${section.name}`}
          />
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-xs truncate">{section.name}</div>
              <div className="text-[10px] text-muted-foreground">
                <FormattedMessage
                  id="docList.duplicates.pageCount"
                  defaultMessage="{count} {count, plural, one {page} other {pages}}"
                  values={{ count: section.pageCount }}
                />
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" align="start">
            <div className="max-w-[300px] break-words">{section.name}</div>
          </TooltipContent>
        </Tooltip>
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={onScroll}
        className="flex-1 overflow-auto p-4 bg-background"
      >
        {pagesToShow.length > 0 ? (
          <div ref={contentRef} className="space-y-4">
            {pagesToShow.map((page, pageIndex) => {
              const imageKey = `${section.id}-${pageIndex}`;
              const isLoaded = loadedImages.has(imageKey);

              return (
                <PageImage
                  key={pageIndex}
                  page={page}
                  section={section}
                  pageIndex={pageIndex}
                  imageKey={imageKey}
                  isLoaded={isLoaded}
                  onImageLoad={onImageLoad}
                  populatePageFn={populatePageFn}
                />
              );
            })}
            {Array.from({ length: blankPagesNeeded }).map((_, i) => (
              <div
                key={`blank-${i}`}
                className="w-full border-2 border-dashed border-border rounded flex items-center justify-center text-muted-foreground text-sm"
                style={{ aspectRatio: "8.5 / 11" }}
              >
                —
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            <FormattedMessage
              id="noPreview"
              defaultMessage="No preview available"
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Displays duplicate sections side-by-side for easy comparison.
 * Features:
 * - Shows up to 10 pages from each section initially, with lazy loading on scroll
 * - Synchronized scrolling across all sections (handles different document lengths)
 * - Hover highlighting to identify corresponding sections
 * - Delete button for each section
 * - Uses presigned URLs for high-quality image loading
 */
export const SideBySideComparisonView: FC<SideBySideComparisonViewProps> = ({
  sections,
  onDeleteClick,
  syncEnabled = true,
  onSyncEnabledChange,
}) => {
  const { organizationId, caseId, chatId } = useChatDocumentParams();
  const { mutateAsync: populatePages } = usePopulateBulkDoclistPages();

  const scrollContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const horizontalScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(null);
  const [visiblePageCounts, setVisiblePageCounts] = useState<
    Record<string, number>
  >({});
  const lastScrollTopRef = useRef(0);
  const lastScrollLeftRef = useRef(0);
  const syncingRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [orderedSections, setOrderedSections] =
    useState<DoclistTreeItemSectionsItem[]>(sections);
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(
    new Set()
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const isDraggable = sections.length > 2;
  const showCheckboxes = sections.length >= 2;

  /**
   * Function to populate a page and get presigned URLs
   */
  const populatePageFn = useCallback(
    async (pageId: string, sectionPageId: string): Promise<PresignedUrls> => {
      const result = await populatePages({
        organizationId: organizationId || "",
        projectId: caseId || "",
        chatId: chatId || "",
        data: { sectionPageIds: [sectionPageId] },
      });

      return result[pageId];
    },
    [organizationId, caseId, chatId, populatePages]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setOrderedSections(sections);
    setSelectedSectionIds((prev) => {
      const allowedIds = new Set(sections.map((s) => s.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (allowedIds.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [sections]);

  const handleCheckboxChange = (sectionId: string, checked: boolean) => {
    setSelectedSectionIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(sectionId);
      } else {
        newSet.delete(sectionId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = () => {
    const sectionsToDelete = orderedSections.filter((s) =>
      selectedSectionIds.has(s.id)
    );
    if (sectionsToDelete.length > 0) {
      onDeleteClick(sectionsToDelete);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = orderedSections.findIndex((s) => s.id === active.id);
    const newIndex = orderedSections.findIndex((s) => s.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = [...orderedSections];
      const [removed] = newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, removed);
      setOrderedSections(newOrder);
    }
  };

  const handleScroll = (sourceIndex: number) => {
    const sourceContainer = scrollContainerRefs.current[sourceIndex];
    if (!sourceContainer) return;

    const scrollTop = sourceContainer.scrollTop;
    const scrollHeight = sourceContainer.scrollHeight;
    const clientHeight = sourceContainer.clientHeight;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;

    setIsScrolledToBottom(isAtBottom);

    if (!syncEnabled) return;
    if (syncingRef.current) return;

    lastScrollTopRef.current = scrollTop;
    lastScrollLeftRef.current = sourceContainer.scrollLeft;

    syncingRef.current = true;
    scrollContainerRefs.current.forEach((container, i) => {
      if (i !== sourceIndex && container) {
        container.scrollTop = lastScrollTopRef.current;
        container.scrollLeft = lastScrollLeftRef.current;
      }
    });

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      syncingRef.current = false;
    }, SCROLL_SYNC_DEBOUNCE_MS);
  };

  useEffect(() => {
    const initialCounts: Record<string, number> = {};
    orderedSections.forEach((section) => {
      initialCounts[section.id] = INITIAL_PAGES_TO_DISPLAY;
    });
    setVisiblePageCounts(initialCounts);
  }, [orderedSections]);

  /**
   * Check scroll position and update navigation button states
   */
  const updateScrollButtons = () => {
    const container = horizontalScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  /**
   * Scroll horizontally by exactly one section width (including gap)
   */
  const scrollHorizontal = (direction: "left" | "right") => {
    const container = horizontalScrollRef.current;
    if (!container) return;

    const firstSection = container.querySelector(
      ".flex-shrink-0"
    ) as HTMLElement;
    if (!firstSection) return;

    const sectionWidth = firstSection.offsetWidth;
    const gap = 16; // gap-4 in Tailwind = 1rem = 16px
    const scrollAmount = sectionWidth + gap;

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const container = horizontalScrollRef.current;
    if (!container) return;

    updateScrollButtons();

    const handleHorizontalScroll = () => {
      updateScrollButtons();
    };

    container.addEventListener("scroll", handleHorizontalScroll);
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      container.removeEventListener("scroll", handleHorizontalScroll);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [orderedSections]);

  const handleLoadMore = useCallback(() => {
    setIsLoadingMore(true);

    setTimeout(() => {
      setVisiblePageCounts((prev) => {
        const updated: Record<string, number> = {};
        orderedSections.forEach((section) => {
          updated[section.id] =
            (prev[section.id] || INITIAL_PAGES_TO_DISPLAY) +
            PAGES_TO_LOAD_ON_SCROLL;
        });
        return updated;
      });
      setIsLoadingMore(false);
      setIsScrolledToBottom(false);
    }, 500);
  }, [orderedSections]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (sections.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        <FormattedMessage
          id="docList.duplicates.selectGroup"
          defaultMessage="Select a group to compare pages side by side"
        />
      </div>
    );
  }

  const noneSelected = selectedSectionIds.size === 0;
  const allSectionsSelected =
    selectedSectionIds.size === orderedSections.length;
  const canDeleteSelected = !noneSelected && !allSectionsSelected;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="mb-2 flex items-center justify-between">
        {onSyncEnabledChange && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={syncEnabled}
              onChange={(e) => onSyncEnabledChange(e.target.checked)}
            />
            <FormattedMessage
              id="docList.duplicates.syncScroll"
              defaultMessage="Sync scroll"
            />
          </label>
        )}
        {showCheckboxes && (
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1">
              {canDeleteSelected && (
                <div className="text-sm text-muted-foreground">
                  <FormattedMessage
                    id="docList.duplicates.selectedToDelete"
                    defaultMessage="{count} {count, plural, one {section} other {sections}} to delete"
                    values={{ count: selectedSectionIds.size }}
                  />
                </div>
              )}
              {noneSelected && (
                <div className="text-xs text-amber-600">
                  <FormattedMessage
                    id="docList.duplicates.selectToDelete"
                    defaultMessage="Select sections to delete"
                  />
                </div>
              )}
              {allSectionsSelected && (
                <div className="text-xs text-amber-600">
                  <FormattedMessage
                    id="docList.duplicates.cannotDeleteAll"
                    defaultMessage="At least one section must be kept"
                  />
                </div>
              )}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    className="gap-1"
                    disabled={!canDeleteSelected}
                  >
                    <Trash2 className="h-3 w-3" />
                    <FormattedMessage
                      id="docList.duplicates.deleteSelectedDuplicates"
                      defaultMessage="Delete {count} {count, plural, one {Duplicate} other {Duplicates}}"
                      values={{ count: selectedSectionIds.size }}
                    />
                  </Button>
                </span>
              </TooltipTrigger>
              {(noneSelected || allSectionsSelected) && (
                <TooltipContent>
                  <FormattedMessage
                    id={
                      noneSelected
                        ? "docList.duplicates.selectToDeleteTooltip"
                        : "docList.duplicates.cannotDeleteAllTooltip"
                    }
                    defaultMessage={
                      noneSelected
                        ? "Select at least one section to delete."
                        : "Cannot delete all sections. At least one section must be kept."
                    }
                  />
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        )}
      </div>
      <div className="relative flex-1 min-h-0">
        {/* Navigation buttons */}
        {canScrollLeft && (
          <Button
            variant="outline"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 shadow-lg bg-background hover:bg-accent"
            onClick={() => scrollHorizontal("left")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
        {canScrollRight && (
          <Button
            variant="outline"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 shadow-lg bg-background hover:bg-accent"
            onClick={() => scrollHorizontal("right")}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={orderedSections.map((s) => s.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div
              ref={horizontalScrollRef}
              className="flex gap-4 h-full overflow-x-auto pb-2"
            >
              {orderedSections.map((section, index) => {
                const isHovered = hoveredSectionId === section.id;
                const visibleCount =
                  visiblePageCounts[section.id] || INITIAL_PAGES_TO_DISPLAY;
                const pagesToShow = section.pages?.slice(0, visibleCount) || [];

                const maxVisiblePages = Math.max(
                  ...orderedSections.map((s) => {
                    const count =
                      visiblePageCounts[s.id] || INITIAL_PAGES_TO_DISPLAY;
                    return Math.min(count, s.pages?.length || 0);
                  })
                );
                const currentVisiblePages = pagesToShow.length;
                const blankPagesNeeded = Math.max(
                  0,
                  maxVisiblePages - currentVisiblePages
                );

                return (
                  <SortableSection
                    key={section.id}
                    section={section}
                    isHovered={isHovered}
                    pagesToShow={pagesToShow}
                    blankPagesNeeded={blankPagesNeeded}
                    onMouseEnter={() => setHoveredSectionId(section.id)}
                    onMouseLeave={() => setHoveredSectionId(null)}
                    onScroll={() => handleScroll(index)}
                    scrollContainerRef={(el) =>
                      (scrollContainerRefs.current[index] = el)
                    }
                    contentRef={(el) => (contentRefs.current[index] = el)}
                    loadedImages={loadedImages}
                    onImageLoad={(imageKey) => {
                      setLoadedImages((prev) => {
                        const newSet = new Set(prev);
                        newSet.add(imageKey);
                        return newSet;
                      });
                    }}
                    isDraggable={isDraggable}
                    showCheckbox={showCheckboxes}
                    isSelected={selectedSectionIds.has(section.id)}
                    onCheckboxChange={(checked) =>
                      handleCheckboxChange(section.id, checked)
                    }
                    populatePageFn={populatePageFn}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

        {isScrolledToBottom &&
          orderedSections.some((section) => {
            const visibleCount =
              visiblePageCounts[section.id] || INITIAL_PAGES_TO_DISPLAY;
            return (section.pages?.length || 0) > visibleCount;
          }) && (
            <Button
              variant="outline"
              size="sm"
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 shadow-lg bg-background hover:bg-accent"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              <FormattedMessage
                id={
                  isLoadingMore
                    ? "docList.duplicates.loading"
                    : "docList.duplicates.loadMore"
                }
                defaultMessage={isLoadingMore ? "Loading..." : "Load More"}
              />
            </Button>
          )}
      </div>
    </div>
  );
};
